import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { submissionsApi, decksApi } from '../services/api';
import { Tournament, DeckSubmissionCreate, Deck, DeckSubmitToTournamentRequest } from '../types';

interface DeckSubmissionModalProps {
  tournament: Tournament;
  isOpen: boolean;
  onClose: () => void;
}

const DeckSubmissionModal: React.FC<DeckSubmissionModalProps> = ({ 
  tournament, 
  isOpen, 
  onClose 
}) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [useExistingDeck, setUseExistingDeck] = useState(false);
  const [decks, setDecks] = useState<Deck[]>([]);
  const [selectedDeckId, setSelectedDeckId] = useState<string>('');
  const [loadingDecks, setLoadingDecks] = useState(false);
  const [formData, setFormData] = useState<DeckSubmissionCreate>({
    tournament_id: tournament.id,
    deck_name: '',
    decklist_text: '',
  });

  useEffect(() => {
    if (useExistingDeck && isOpen) {
      fetchDecks();
    }
  }, [useExistingDeck, isOpen]);

  const fetchDecks = async () => {
    try {
      setLoadingDecks(true);
      const response = await decksApi.getAll();
      setDecks(response.data);
    } catch (err) {
      console.error('Failed to fetch decks:', err);
    } finally {
      setLoadingDecks(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      let response;

      if (useExistingDeck && selectedDeckId) {
        // Submit existing deck
        const submitRequest: DeckSubmitToTournamentRequest = {
          tournament_id: tournament.id,
        };
        response = await decksApi.submitToTournament(selectedDeckId, submitRequest);
      } else {
        // Create new submission
        if (!formData.deck_name || !formData.decklist_text) {
          alert('Deck name and decklist are required');
          return;
        }
        response = await submissionsApi.create(formData);
      }
      
      // Check if deck is over budget
      const isOverBudget = response.data.validation_errors?.some((error: string) => 
        error.includes('exceeds budget limit')
      );
      
      if (isOverBudget) {
        alert('Deck saved as draft. Deck exceeds budget and cannot be submitted for review until corrected.');
      } else {
        alert('Deck saved as draft successfully!');
      }
      
      // Close modal and redirect to deck details page
      onClose();
      navigate(`/deck/${response.data.id}`);
    } catch (error: any) {
      console.error('Failed to submit deck:', error);
      alert(error.response?.data?.detail || 'Failed to submit deck. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">
                Submit Deck to {tournament.name}
              </h2>
              <button
                onClick={handleClose}
                disabled={submitting}
                className="text-gray-500 hover:text-gray-700 text-2xl disabled:opacity-50"
              >
                ×
              </button>
            </div>

            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">Tournament Rules</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Budget Limit:</span> €{tournament.ruleset?.budget_limit}
                </div>
                <div>
                  <span className="font-medium">Format:</span> {tournament.ruleset?.format}
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Toggle between existing deck and new deck */}
              <div className="flex gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => setUseExistingDeck(false)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    !useExistingDeck
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Create New Deck
                </button>
                <button
                  type="button"
                  onClick={() => setUseExistingDeck(true)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    useExistingDeck
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Choose from My Decks
                </button>
              </div>

              {useExistingDeck ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Deck *
                  </label>
                  {loadingDecks ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-600">Loading decks...</p>
                    </div>
                  ) : (
                    <select
                      value={selectedDeckId}
                      onChange={(e) => setSelectedDeckId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">-- Select a deck --</option>
                      {decks.map((deck) => (
                        <option key={deck.id} value={deck.id}>
                          {deck.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {decks.length === 0 && !loadingDecks && (
                    <p className="text-sm text-gray-500 mt-2">
                      You don't have any decks yet.{' '}
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          navigate('/deck-editor');
                        }}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Create one now
                      </button>
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deck Name *
                    </label>
                    <input
                      type="text"
                      value={formData.deck_name}
                      onChange={(e) => setFormData({ ...formData, deck_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., My Budget Commander"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Decklist *
                    </label>
                    <textarea
                      value={formData.decklist_text}
                      onChange={(e) => setFormData({ ...formData, decklist_text: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={15}
                      placeholder="Paste your decklist here (one card per line, format: quantity cardname)&#10;&#10;Example:&#10;1 Sol Ring&#10;4 Lightning Bolt&#10;1 Lightning Bolt (M21)"
                      required
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Format: One card per line, starting with quantity (e.g., "4 Lightning Bolt")
                    </p>
                  </div>
                </>
              )}

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Deck'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={submitting}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeckSubmissionModal;
